import { DateTime } from 'luxon';
import { appState } from '../app.state';
import type { CellWidthView } from '../app.state';
import type { FiltersDTO } from '../domain/dtos/filters.dto';
import { ActionIds, FOCUS_TODAY_EVENT } from './action.types';
import { boardSortStorageKey, nextBoardSortChoice } from '../helpers/board-sort.helper';
import { tenantBoardSortDefault } from '../tenant-feature-flags';
import type { ActionRegistryImpl } from './action-registry';

/**
 * Registers all view-state actions with the given registry.
 *
 * View-state actions mutate frontend state only (appState) without backend calls.
 * Includes: sidebar.toggle.todo, sidebar.toggle.knowledge, timeline.jumpToToday,
 * timeline.jumpToDate, timeline.setCellWidthView, board.toggleSort, filters.clear.
 *
 * @param registry - The ActionRegistryImpl to register actions with.
 */
export function registerViewStateActions(registry: ActionRegistryImpl): void {
    registry.register({
        id: ActionIds.SIDEBAR_TOGGLE_TODO,
        label: 'Toggle ToDo Filter Drawer',
        category: 'view',
        execute: () => {
            appState.todoFilterOpen = !appState.todoFilterOpen;
        },
    });

    registry.register({
        id: ActionIds.SIDEBAR_TOGGLE_KNOWLEDGE,
        label: 'Toggle Knowledge Filter Drawer',
        category: 'view',
        execute: () => {
            appState.knowledgeFilterOpen = !appState.knowledgeFilterOpen;
        },
    });

    registry.register({
        id: ActionIds.TIMELINE_JUMP_TO_TODAY,
        label: 'Jump to Today',
        category: 'navigation',
        execute: () => {
            // In detail view (wide cells, few days visible) only one day of context
            // is shown before today; in the standard overview the cursor is offset
            // by 5 days so today lands further to the right.
            const offsetDays = appState.isWeekView() ? 1 : 5;
            appState.dateCursorDate = DateTime.utc().startOf('day').minus({ day: offsetDays });
            // Dispatch window event AFTER appState update (bridge fires synchronously above)
            window.dispatchEvent(new CustomEvent(FOCUS_TODAY_EVENT));
        },
    });

    registry.register<DateTime>({
        id: ActionIds.TIMELINE_JUMP_TO_DATE,
        label: 'Jump to Date',
        category: 'navigation',
        execute: (payload: DateTime) => {
            appState.dateCursorDate = payload;
        },
    });

    registry.register<CellWidthView>({
        id: ActionIds.TIMELINE_SET_CELL_WIDTH_VIEW,
        label: 'Set Cell Width View',
        category: 'view',
        execute: (view: CellWidthView) => {
            appState.setCellWidthView(view);
        },
    });

    registry.register({
        id: ActionIds.BOARD_TOGGLE_SORT,
        label: 'Toggle Project Board Sort',
        category: 'view',
        execute: () => {
            const next = nextBoardSortChoice(appState.boardSort, tenantBoardSortDefault(globalThis.window?.__FEATURE_FLAGS__));
            appState.boardSort = next;
            if (next === null) {
                // Back on the tenant default: forget the choice (Anke's decision 6). Setting null
                // above notifies subscribers synchronously; removing the key keeps the store empty
                // until the next start (where @storage writes the "null" default back anyway).
                globalThis.localStorage?.removeItem(boardSortStorageKey());
            }
        },
    });

    registry.register({
        id: ActionIds.FILTERS_CLEAR,
        label: 'Clear Filters',
        category: 'view',
        execute: () => {
            appState.filters = {} as FiltersDTO;
        },
    });

    registry.register({
        id: ActionIds.RESOURCE_FILTERS_CLEAR,
        label: 'Clear Resource Filters',
        category: 'view',
        // The resource slot reuses FiltersDTO verbatim (identical shape to ToDo),
        // so this clears appState.resourceFilters only — the ToDo filters slot is untouched.
        execute: () => {
            appState.resourceFilters = {} as FiltersDTO;
        },
    });
}
